import React from "react";
import { formatBRL } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Check, Plus, Trash2, FileText, Calculator, Loader2, AlertTriangle, Building2 } from "lucide-react";
import {
  ADVANCE_PERCENT_APPROX_HINT,
  ADVANCE_PERCENT_HINT,
  advanceExceedsFace,
  advanceExceedsFaceCopy,
  advancePercentDisabledHint,
} from "../../lib/operations";
import {
  CLIENTE_INDISPONIVEL_COPY,
  hasVisibleLiquidValue,
  SCOPE_PRICING,
  SACADO_LABEL,
  SACADO_LABEL_SUFFIX,
} from "../../lib/cedenteOperations";
import ClienteFormDialog from "../ClienteFormDialog";
import { cedentePath } from "../../lib/portal";
import { BACKING_TYPE_OPTIONS, DEFAULT_BACKING_TYPE } from "../../lib/backingTypes";
import { maskMoneyCentsBR, maskPctBR } from "../../lib/numberFormat";
import { CAMPO_FALTANDO_CLS, CAMPO_FALTANDO_CLS_SELECT, HINT_CENTAVOS } from "./config";

// R5 — extraído MECANICAMENTE de pages/NewOperation.jsx (JSX verbatim; o
// estado vive no componente-pai e chega por `ctx`).
export default function StepRecebiveis({ ctx }) {
  const {
    cfg,
    isCedenteMode,
    clientes,
    deepLinkUnresolved,
    clienteDialog,
    setClienteDialog,
    simulating,
    simulationResult,
    simulationError,
    terminalError,
    terminalRef,
    descricao,
    setDescricao,
    receivables,
    addReceivable,
    removeReceivable,
    updateReceivable,
    updateNetFaceValue,
    updateAdvanceValue,
    updateAdvancePercent,
    blurAdvancePercent,
    finalizeMoneyField,
    percentualIndisponivel,
    percentualTexto,
    percentualAproximado,
    selectPayerCliente,
    handleClienteCreated,
    openClienteDialog,
    faltando,
    rotulosFaltando,
    rotulosVazios,
    rotulosInvalidos,
    payerInvalido,
    validReceivables,
    hasPartialReceivable,
    avisoRecebivelParcial,
    totalReceivables,
    runSimulation,
    policyRejected,
    allowedRangeText,
    policyRejectText,
    oprGrossValue,
    oprLiquidValue,
    oprDiscountValue,
    semClientes,
    rascunhoDisponivel,
    requestExit,
  } = ctx;
  return (
    <div className="zemo-card p-6 space-y-5" data-testid="step-3-receivables">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Recebiveis (NFs / Duplicatas)</h2>
          <p className="text-sm text-zinc-500 mt-0.5">
            {cfg.copy.recebiveisHint}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={addReceivable} className="gap-1.5" data-testid="add-receivable-btn">
          <Plus className="h-3.5 w-3.5" /> Adicionar
        </Button>
      </div>

      {/* BLOQUEIO TERMINAL (modo cedente): a condição não depende do que o
          usuário digita — nenhum ajuste de valor/prazo resolve. Para aqui,
          com foco programático, em vez de deixar chegar à Revisão. */}
      {terminalError && (
        <div
          role="alert"
          ref={terminalRef}
          tabIndex={-1}
          className="rounded-md p-4 border border-red-200 bg-red-50 text-[13px] text-red-800 space-y-1 outline-none focus:ring-2 focus:ring-red-300"
          data-testid="cedente-terminal-error"
        >
          <div className="flex items-start gap-1.5 font-medium">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{terminalError.message}</span>
          </div>
          {/* Só o bloqueio de PREÇO tem saída no wizard (outro produto).
              No de CONTA não há o que o usuário mexa aqui — não sugerir. */}
          {terminalError.scope === SCOPE_PRICING && (
            <div className="text-red-700">
              Você pode voltar e escolher outro produto — os dados já preenchidos são mantidos.
            </div>
          )}
        </div>
      )}

      {/* Sem cliente cadastrado não há sacado possível (modo cedente). */}
      {semClientes && (
        <div className="rounded-md p-4 border border-amber-200 bg-amber-50 text-[13px] text-amber-800" data-testid="clientes-empty">
          <div className="flex items-start gap-2">
            <Building2 className="h-4 w-4 mt-0.5 shrink-0" />
            <div className="space-y-2">
              <div>
                Você ainda não tem cliente cadastrado — e todo recebível precisa de um sacado.
                Cadastre o primeiro aqui mesmo, sem sair da operação.
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  className="gap-1.5 bg-[#0055FF] hover:bg-[#0044CC] text-white"
                  onClick={() => openClienteDialog(receivables[0]?._key, "receivable-payer-cliente-0")}
                  data-testid="clientes-empty-inline-cta"
                >
                  <Plus className="h-3.5 w-3.5" /> Cadastrar cliente
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => requestExit(cedentePath("/clientes"))}
                  data-testid="clientes-empty-cta"
                >
                  Ir para Clientes
                </Button>
              </div>
              {/* FS-161: com o rascunho, sair daqui deixou de perder o que
                  foi digitado — a frase antiga ("os dados são perdidos")
                  passaria a mentir. Round 2 / U2: e a nova só vale quando há
                  rascunho de verdade; sem ele, o aviso original é o honesto.
                  O cadastro inline segue sendo o caminho curto: não sai da
                  tela em nenhum dos dois casos. */}
              <div className="text-amber-900" data-testid="clientes-empty-perda-copy">
                {rascunhoDisponivel ? (
                  <>
                    Atenção: ir para <strong>Clientes</strong> sai desta tela. O que você já
                    preencheu fica guardado como rascunho nesta aba e é oferecido de volta
                    quando você retornar — o cadastro acima nem sai daqui.
                  </>
                ) : (
                  <>
                    Atenção: ir para <strong>Clientes</strong> sai desta tela e os dados já
                    preenchidos aqui são perdidos (não foi possível guardar rascunho neste
                    navegador) — o cadastro acima mantém tudo.
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Deep-link com cliente que não existe mais / fora da lista. */}
      {isCedenteMode && deepLinkUnresolved && !semClientes && (
        <div role="alert" className="rounded-md p-3 border border-amber-200 bg-amber-50 text-[13px] text-amber-800" data-testid="cliente-indisponivel">
          {CLIENTE_INDISPONIVEL_COPY}
        </div>
      )}

      <div className="space-y-4">
        {receivables.map((r, idx) => (
          <div key={r._key} className="relative border border-[var(--zemo-border)] rounded-lg p-4 bg-zinc-50/30" data-testid={`receivable-row-${idx}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-zinc-700">
                <FileText className="h-4 w-4 text-[#0055FF]" />
                Recebivel #{idx + 1}
              </div>
              {receivables.length > 1 && (
                <button onClick={() => removeReceivable(r._key)} className="text-zinc-400 hover:text-red-500 transition-colors" data-testid={`remove-receivable-${idx}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-[12px] text-zinc-500">ID externo (NF)</Label>
                <Input value={r.external_id} onChange={(e) => updateReceivable(r._key, "external_id", e.target.value)} placeholder="NF-001" className={`h-9 text-sm ${faltando(r, "external_id") ? CAMPO_FALTANDO_CLS : ""}`} aria-invalid={faltando(r, "external_id") || undefined} aria-describedby={faltando(r, "external_id") ? `receivable-erro-${idx}` : undefined} data-testid={`receivable-external-id-${idx}`} />
              </div>
              {cfg.payerFromClientes ? (
                /* Sacado = cliente do cadastro do cedente (sem digitação
                   livre): a seleção preenche payer_name/payer_document, e o
                   payload enviado é idêntico ao do modo parceiro. */
                <div className="space-y-1 sm:col-span-2">
                  {/* Rótulo da superfície de sacado (decisão do owner):
                      "clientes do cedente SÃO os sacados da operação". */}
                  <Label className="text-[12px] text-zinc-500" htmlFor={`receivable-payer-cliente-${idx}`}>
                    {SACADO_LABEL}{" "}
                    <span className="text-[11px] text-zinc-400 font-normal">{SACADO_LABEL_SUFFIX}</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={r.payer_cliente_id || ""}
                      onValueChange={(v) => selectPayerCliente(r._key, v)}
                    >
                      <SelectTrigger
                        id={`receivable-payer-cliente-${idx}`}
                        className={`h-9 text-sm ${payerInvalido(r) ? CAMPO_FALTANDO_CLS_SELECT : ""}`}
                        /* Só marca inválido depois que a linha foi mexida:
                           um recebível novo, ainda em branco, não é "erro"
                           — o leitor de tela anunciaria falha no pristine. */
                        aria-invalid={payerInvalido(r) || undefined}
                        aria-describedby={payerInvalido(r) ? `receivable-payer-erro-${idx}` : undefined}
                        data-testid={`receivable-payer-cliente-${idx}`}
                      >
                        <SelectValue placeholder="Selecione o cliente..." />
                      </SelectTrigger>
                      <SelectContent>
                        {clientes.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{c.razao_social}</span>
                              <span className="text-[11px] text-zinc-500 font-mono-num">{c.cnpj}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* Cadastro INLINE (Q5): o recém-criado já entra NESTA
                        linha — sem sair do wizard, sem perder o rascunho. */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 gap-1.5 shrink-0"
                      onClick={() => openClienteDialog(r._key, `receivable-payer-cliente-${idx}`)}
                      aria-label={`Cadastrar cliente para o recebível ${idx + 1}`}
                      data-testid={`receivable-add-cliente-${idx}`}
                    >
                      <Plus className="h-3.5 w-3.5" /> Cadastrar cliente
                    </Button>
                  </div>
                  {payerInvalido(r) && (
                    <p id={`receivable-payer-erro-${idx}`} className="text-[12px] text-red-600">
                      Selecione o cliente que é o sacado deste recebível.
                    </p>
                  )}
                </div>
              ) : (
                <>
                  <div className="space-y-1">
                    <Label className="text-[12px] text-zinc-500">Sacado (pagador)</Label>
                    <Input value={r.payer_name} onChange={(e) => updateReceivable(r._key, "payer_name", e.target.value)} placeholder="Empresa XYZ Ltda" className={`h-9 text-sm ${faltando(r, "payer_name") ? CAMPO_FALTANDO_CLS : ""}`} aria-invalid={faltando(r, "payer_name") || undefined} aria-describedby={faltando(r, "payer_name") ? `receivable-erro-${idx}` : undefined} data-testid={`receivable-payer-name-${idx}`} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[12px] text-zinc-500">CNPJ/CPF do sacado</Label>
                    <Input value={r.payer_document} onChange={(e) => updateReceivable(r._key, "payer_document", e.target.value)} placeholder="11.222.333/0001-99" className={`h-9 text-sm font-mono-num ${faltando(r, "payer_document") ? CAMPO_FALTANDO_CLS : ""}`} aria-invalid={faltando(r, "payer_document") || undefined} aria-describedby={faltando(r, "payer_document") ? `receivable-erro-${idx}` : undefined} data-testid={`receivable-payer-doc-${idx}`} />
                  </div>
                </>
              )}
              <div className="space-y-1">
                <Label htmlFor={`receivable-net-value-${idx}`} className="text-[12px] text-zinc-500">{cfg.copy.valorNotaLabel}</Label>
                <Input id={`receivable-net-value-${idx}`} type="text" inputMode="numeric" value={r.net_face_value} onChange={(e) => updateNetFaceValue(r._key, maskMoneyCentsBR(e.target.value, r.net_face_value))} onBlur={(e) => finalizeMoneyField(r._key, "net_face_value", e.target.value)} placeholder="R$ 10.000,00" className={`h-9 text-sm font-mono-num ${faltando(r, "net_face_value") ? CAMPO_FALTANDO_CLS : ""}`} aria-invalid={faltando(r, "net_face_value") || undefined} aria-describedby={[`receivable-net-value-hint-${idx}`, cfg.copy.valorNotaHelp ? `receivable-net-value-help-${idx}` : null, faltando(r, "net_face_value") ? `receivable-erro-${idx}` : null].filter(Boolean).join(" ")} data-testid={`receivable-net-value-${idx}`} />
                {/* A indicação de que o valor JÁ é líquido de Impostos e
                    Glosa — helper discreto. FS-159/P3: passou a valer nos
                    DOIS modos (era só no cedente, item 09.2). O nó segue
                    condicional: `valorNotaHelp` null não renderiza nada.
                    MERGE FS-160: o rótulo do MODO (FS-159) e o helper 09.2
                    convivem com o hint da registradora — o `htmlFor`/`id`
                    deram nome acessível ao campo e o `aria-describedby`
                    soma hint + helper + erro, nesta ordem. */}
                {cfg.copy.valorNotaHelp && (
                  <p id={`receivable-net-value-help-${idx}`} className="text-[11px] text-zinc-400">
                    {cfg.copy.valorNotaHelp}
                  </p>
                )}
                <p id={`receivable-net-value-hint-${idx}`} className="text-[11px] text-zinc-400" data-testid={`receivable-net-value-hint-${idx}`}>
                  {HINT_CENTAVOS}
                </p>
              </div>
              {/* FS-162 — "Percentual antecipado" (Q7: nos 2 modos), ENTRE
                  o valor de face e o valor a antecipar: base → percentual →
                  resultado, na ordem em que a conta se lê.

                  ⚠️ REGIMES DE TECLADO OPOSTOS lado a lado, por decisão do
                  owner (Q5, ratificada): no campo de reais "100" é R$ 1,00
                  (caixa registradora) e aqui "100" é 100%. Cada um declara o
                  seu no PRÓPRIO hint, também por `aria-describedby` — a
                  divergência é deliberada, mas não pode ser implícita.

                  `maskPctBR` (decimal simples, a mesma do campo de taxa),
                  NUNCA `maskMoneyCentsBR`: percentual não tem centavos.
                  Desabilitado enquanto o valor de face não for legível —
                  "NaN%" num campo de dinheiro é pior que campo apagado —, e
                  o hint DIZ o porquê (UX-5), em vez de seguir ensinando a
                  digitar num campo em que não se digita. */}
              <div className="space-y-1">
                <Label htmlFor={`receivable-advance-pct-${idx}`} className="text-[12px] text-zinc-500">{cfg.copy.percentualLabel}</Label>
                <Input
                  id={`receivable-advance-pct-${idx}`}
                  type="text"
                  inputMode="decimal"
                  value={percentualTexto(r)}
                  disabled={percentualIndisponivel(r)}
                  title={percentualIndisponivel(r) ? advancePercentDisabledHint(cfg.copy.valorNotaLabelCurto) : undefined}
                  onChange={(e) => updateAdvancePercent(r._key, maskPctBR(e.target.value, percentualTexto(r)))}
                  onBlur={() => blurAdvancePercent(r._key)}
                  placeholder="100,00"
                  className={`h-9 text-sm font-mono-num ${faltando(r, "requested_advance_value") || advanceExceedsFace(r) ? CAMPO_FALTANDO_CLS : ""}`}
                  /* UI-1: o campo % participa do estado de erro do teto. Ele
                     exibe o número fora de faixa ("150,00") e é um dos dois
                     controles que resolvem — ficar neutro e sem descrição
                     deixava o erro visível só no campo vizinho. */
                  aria-invalid={faltando(r, "requested_advance_value") || advanceExceedsFace(r) || undefined}
                  aria-describedby={[
                    `receivable-advance-pct-hint-${idx}`,
                    percentualAproximado(r) ? `receivable-advance-pct-approx-${idx}` : null,
                    advanceExceedsFace(r) ? `receivable-advance-exceeds-${idx}` : null,
                    faltando(r, "requested_advance_value") ? `receivable-erro-${idx}` : null,
                  ].filter(Boolean).join(" ")}
                  data-testid={`receivable-advance-pct-${idx}`}
                />
                {/* UI-3: `zinc-500` (≈4,6:1) em vez do `zinc-400` (≈2,7:1,
                    reprova AA a 11px) — e os dois hints deste campo passam a
                    usar o MESMO cinza, em vez de dois a 4 pixels um do
                    outro. */}
                <p id={`receivable-advance-pct-hint-${idx}`} className="text-[11px] text-zinc-500" data-testid={`receivable-advance-pct-hint-${idx}`}>
                  {percentualIndisponivel(r)
                    ? advancePercentDisabledHint(cfg.copy.valorNotaLabelCurto)
                    : ADVANCE_PERCENT_HINT}
                </p>
                {/* Q2 — o % exibido tem 2 casas e é TRUNCADO: quando não
                    representa o valor exatamente, a tela DIZ. Sem isto o
                    usuário leria "49,84%" como se fosse a conta fechada.

                    UI-2: o nó EXISTE sempre (altura reservada) e é uma
                    região viva. Como `<p>` condicional ele (a) fazia a linha
                    seguinte do grid pular ao aparecer/sumir durante a
                    digitação no campo vizinho e (b) era inalcançável por
                    leitor de tela — `aria-describedby` só é lido ao GANHAR
                    foco, e o aviso nasce sempre com o foco em outro lugar.
                    `aria-live` anuncia na MUDANÇA de conteúdo, então texto
                    estável não vira tagarelice. */}
                <p
                  id={`receivable-advance-pct-approx-slot-${idx}`}
                  role="status"
                  aria-live="polite"
                  className="text-[11px] text-zinc-500 min-h-[15px]"
                  data-testid={`receivable-advance-pct-approx-slot-${idx}`}
                >
                  {percentualAproximado(r) && (
                    <span id={`receivable-advance-pct-approx-${idx}`} data-testid={`receivable-advance-pct-approx-${idx}`}>
                      {ADVANCE_PERCENT_APPROX_HINT}
                    </span>
                  )}
                </p>
              </div>
              <div className="space-y-1">
                <Label htmlFor={`receivable-advance-value-${idx}`} className="text-[12px] text-zinc-500">Valor a antecipar (R$)</Label>
                <Input id={`receivable-advance-value-${idx}`} type="text" inputMode="numeric" value={r.requested_advance_value} onChange={(e) => updateAdvanceValue(r._key, maskMoneyCentsBR(e.target.value, r.requested_advance_value))} onBlur={(e) => finalizeMoneyField(r._key, "requested_advance_value", e.target.value)} placeholder="R$ 9.500,00" className={`h-9 text-sm font-mono-num ${faltando(r, "requested_advance_value") || advanceExceedsFace(r) ? CAMPO_FALTANDO_CLS : ""}`} aria-invalid={faltando(r, "requested_advance_value") || advanceExceedsFace(r) || undefined} aria-describedby={[`receivable-advance-value-hint-${idx}`, advanceExceedsFace(r) ? `receivable-advance-exceeds-${idx}` : null, faltando(r, "requested_advance_value") ? `receivable-erro-${idx}` : null].filter(Boolean).join(" ")} data-testid={`receivable-advance-value-${idx}`} />
                <p id={`receivable-advance-value-hint-${idx}`} className="text-[11px] text-zinc-400" data-testid={`receivable-advance-value-hint-${idx}`}>
                  {HINT_CENTAVOS}
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-[12px] text-zinc-500">Data de vencimento</Label>
                <Input type="date" value={r.due_date} onChange={(e) => updateReceivable(r._key, "due_date", e.target.value)} className={`h-9 text-sm font-mono-num ${faltando(r, "due_date") ? CAMPO_FALTANDO_CLS : ""}`} aria-invalid={faltando(r, "due_date") || undefined} aria-describedby={faltando(r, "due_date") ? `receivable-erro-${idx}` : undefined} data-testid={`receivable-date-${idx}`} />
              </div>
              {/* Tipo de lastro POR recebível (item 23c): 25 valores do enum da V2
                  (constante única em lib/backingTypes.js, compartilhada com o
                  Estoque). Default NFE = comportamento histórico do create. */}
              <div className="space-y-1">
                <Label className="text-[12px] text-zinc-500">Tipo de Lastro</Label>
                <Select value={r.backing_type || DEFAULT_BACKING_TYPE} onValueChange={(v) => updateReceivable(r._key, "backing_type", v)}>
                  <SelectTrigger className="h-9 text-sm" data-testid={`receivable-backing-type-${idx}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BACKING_TYPE_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Item E: a LINHA diz quais campos faltam. O banner do passo só
                dizia "há recebível incompleto" e o usuário tinha de caçar o
                campo. É também o alvo do `aria-describedby` dos campos
                marcados: sem texto associado, borda vermelha não existe
                para leitor de tela. Sem `role="alert"` de propósito — a
                lista muda a cada tecla e seria interrupção contínua; quem
                anuncia é o banner do passo, que já tem `role="alert"`. */}
            {rotulosFaltando(r).length > 0 && (
              <p
                id={`receivable-erro-${idx}`}
                className="mt-3 text-[12px] text-red-600"
                data-testid={`receivable-erro-${idx}`}
              >
                {rotulosVazios(r).length > 0 && (
                  <>Falta preencher: {rotulosVazios(r).join(", ")}.</>
                )}
                {rotulosVazios(r).length > 0 && rotulosInvalidos(r).length > 0 && " "}
                {/* FS-160 (UX-5): frase PRÓPRIA para o campo escrito com
                    valor que não é número — "falta preencher" com o texto
                    visível no campo mandava o usuário procurar o que já
                    estava lá. */}
                {rotulosInvalidos(r).length > 0 && (
                  <>Valor inválido em: {rotulosInvalidos(r).join(", ")}.</>
                )}
              </p>
            )}
            {/* FS-162 / Q4 — TETO do antecipável na linha. Frase PRÓPRIA, e
                não "Falta preencher"/"Valor inválido": os dois campos estão
                escritos e são números válidos; o que está errado é a relação
                entre eles. O NFV é o teto por definição (vault
                net-face-value) e a V2 recusa com 422 — sem este espelho, o
                usuário atravessava o wizard inteiro para descobrir no
                create. A linha já saiu de `validReceivables`, então o passo
                trava pelo gate que já existia. */}
            {advanceExceedsFace(r) && (
              <p
                id={`receivable-advance-exceeds-${idx}`}
                /* UI-1 — REGIÃO VIVA. Diferente da lista de campos faltando
                   (que muda a cada tecla e por isso não é anunciada), este é
                   um predicado ESTÁVEL: ou o valor passa do teto ou não.
                   Sem isto, a única frase anunciada era a do banner — e o
                   usuário de leitor de tela não era informado do erro que
                   trava o passo, porque `aria-describedby` só é lido ao
                   GANHAR foco e a mensagem nasce com o campo já focado. */
                role="status"
                aria-live="polite"
                className="mt-3 text-[12px] text-red-600"
                data-testid={`receivable-advance-exceeds-${idx}`}
              >
                {advanceExceedsFaceCopy(cfg.copy.valorNotaLabelCurto)}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Descrição: no modo cedente não existe a etapa "Cedente & Taxa",
          onde este campo morava. */}
      {!cfg.hasCedenteStep && (
        <div className="space-y-1.5">
          <Label className="text-[13px] font-medium" htmlFor="input-descricao">Descrição (opcional)</Label>
          <Textarea id="input-descricao" value={descricao} onChange={(e) => setDescricao(e.target.value)} placeholder="Observações sobre esta operação..." rows={3} data-testid="input-descricao" />
        </div>
      )}

      {/* Summary + Simulate */}
      <div className="flex items-center justify-between p-3 rounded-md bg-zinc-100 border border-[var(--zemo-border)]">
        <span className="text-sm text-zinc-600">
          {validReceivables.length} recebive(is) valido(s) de {receivables.length}
        </span>
        <span className="text-sm font-semibold text-zinc-900 font-mono-num">
          Total a antecipar: {formatBRL(totalReceivables)}
        </span>
      </div>

      {/* Simulacao automatica (debounce): o botao segue como fallback manual */}
      {validReceivables.length > 0 && !terminalError && (
        <Button
          variant="outline"
          onClick={runSimulation}
          disabled={simulating}
          className="w-full gap-2 h-11 border-[#0055FF]/30 text-[#0055FF] hover:bg-[#0055FF]/5"
          data-testid="simulate-btn"
        >
          {simulating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />}
          {simulating
            ? (isCedenteMode ? "Calculando..." : "Simulando na V2...")
            : (isCedenteMode ? "Recalcular valor líquido" : "Recalcular simulacao (V2)")}
        </Button>
      )}

      {/* Erro da simulacao — transitório/acionável (o terminal tem card próprio) */}
      {simulationError && !simulating && (
        <div role="alert" className="rounded-md p-3 border border-red-200 bg-red-50 text-[13px] text-red-800" data-testid="simulation-error">
          {simulationError}
        </div>
      )}

      {/* Veredito consultivo REJEITADO (item 16): a simulacao respondeu 200
          com os numeros, mas o create recusaria (would_reject). No modo
          parceiro sai o motivo da V2 + faixa; no modo cedente (01(b)) sai
          copy genérica, SEM faixa e sem percentual. */}
      {policyRejected && (
        <div role="alert" className="rounded-md p-3 border border-red-200 bg-red-50 text-[13px] text-red-800 space-y-1" data-testid="policy-verdict-rejected">
          <div className="flex items-start gap-1.5 font-medium">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{policyRejectText}</span>
          </div>
          {allowedRangeText && (
            <div className="font-mono-num" data-testid="policy-verdict-range">{allowedRangeText}</div>
          )}
          {cfg.showRates && (
            <div className="text-red-700">
              Ajuste a taxa mensal ou os valores dos recebiveis para continuar —
              a operacao seria recusada pela politica de taxas do produto como esta.
            </div>
          )}
        </div>
      )}

      {/* Simulation result */}
      {simulationResult && (
        <div role="status" aria-live="polite" className="rounded-lg border border-green-200 bg-green-50 p-4 space-y-3" data-testid="simulation-result">
          <div className="flex items-center gap-2 text-sm font-semibold text-green-800">
            <Check className="h-4 w-4" /> {isCedenteMode ? "Valor da operação" : "Resultado da simulacao V2"}
            <span className="font-normal text-[11px] text-green-700">(recalculada automaticamente ao alterar os recebiveis)</span>
          </div>
          {/* Modo reduzido (01(b)): o cedente vê SÓ valor líquido e nº de
              notas — sem valor bruto, sem desconto discriminado, sem prazo
              médio, sem hierarquia de taxa (tudo isso permite derivar %). */}
          <div className="grid sm:grid-cols-3 gap-3">
            {(isCedenteMode
              ? [
                  ["Valor liquido", oprLiquidValue],
                  ["Numero de notas", validReceivables.length],
                ]
              : [
                  ["Valor bruto", oprGrossValue],
                  ["Valor liquido", oprLiquidValue],
                  ["Desconto total", oprDiscountValue],
                ]
            ).map(([label, val]) => (
              <div key={label} className="p-2 bg-white rounded border border-green-100">
                <div className="text-[11px] text-green-700 uppercase tracking-wide">{label}</div>
                <div className="mt-0.5 font-semibold font-mono-num text-green-900">
                  {label === "Numero de notas"
                    ? val
                    /* O endurecimento do money-path (BLOQ-3) vale SÓ no modo
                       cedente: no parceiro um desconto "0.00" é um número
                       legítimo, e exibi-lo como "--" mudaria a tela dele. */
                    : ((isCedenteMode ? hasVisibleLiquidValue(val) : val)
                        ? formatBRL(parseFloat(val)) : "--")}
                </div>
              </div>
            ))}
          </div>
          {cfg.showRates && simulationResult.average_days_in_advance && (
            <div className="text-[12px] text-green-700">
              Prazo medio: {simulationResult.average_days_in_advance} dias
              {simulationResult.fee_hierarchy && ` | Hierarquia: ${simulationResult.fee_hierarchy}`}
            </div>
          )}
          {/* Aprovado com banda presente (item 16): faixa DISCRETA, so
              informativa — o usuario sabe ate onde pode mexer na taxa. */}
          {!policyRejected && allowedRangeText && (
            <div className="text-[12px] text-green-700 font-mono-num" data-testid="policy-allowed-range-info">
              {allowedRangeText}
            </div>
          )}
        </div>
      )}

      {/* Linha parcial bloqueia o Continuar (nada e descartado em silencio) */}
      {hasPartialReceivable && (
        <div role="alert" className="rounded-md p-3 border border-amber-200 bg-amber-50 text-[13px] text-amber-800" data-testid="partial-receivable-warning">
          {avisoRecebivelParcial}
        </div>
      )}

      {validReceivables.length === 0 && !hasPartialReceivable && !semClientes && (
        <div className="rounded-md p-3 border border-zinc-200 bg-zinc-50 text-[13px] text-zinc-600">
          {cfg.copy.semRecebiveis}
        </div>
      )}

      {/* Cadastro inline de cliente/sacado (Q5) — mesma mecânica do dialog
          de representante: cria, recarrega e seleciona na linha de origem. */}
      {cfg.payerFromClientes && (
        <ClienteFormDialog
          open={clienteDialog.open}
          onOpenChange={(open) => setClienteDialog((d) => ({ ...d, open }))}
          returnFocusTo={clienteDialog.triggerId}
          onCreated={handleClienteCreated}
        />
      )}
    </div>
  );
}
